import { AIMessage } from '@/types/ai';

/**
 * Simulate streaming text effect
 * @param text - Text to stream
 * @param typingSpeed - Speed of typing in milliseconds
 * @param onToken - Callback for each token
 * @returns Promise that resolves when streaming is complete
 */
export const simulateStreamingText = async (
  text: string,
  typingSpeed: number = 30,
  onToken?: (token: string, accumulated: string) => void
): Promise<string> => {
  let accumulated = '';

  // Split text into words and punctuation
  const tokens = text.match(/\S+|\s+|[,.!?;:]/g) || [];

  for (const token of tokens) {
    accumulated += token;

    if (onToken) {
      onToken(token, accumulated);
    }

    // Add random variation to typing speed
    const randomDelay = typingSpeed * (0.5 + Math.random());
    await new Promise(resolve => setTimeout(resolve, randomDelay));
  }

  return accumulated;
};

/**
 * Convert our message format to the format expected by the AI API
 */
export const formatMessagesForAI = (messages: any[]): AIMessage[] => {
  return messages
    .filter(msg => msg && (msg.type !== 'system' || (msg as any).visible !== false))
    .map(msg => ({
      role: msg.sender === 'user' ? 'user' : 'assistant' as 'user' | 'assistant' | 'system',
      content: msg.text || '',
    }));
};

/**
 * Generate system prompt based on chatbot configuration
 * Provides context for the AI model
 */
export const generateSystemPrompt = (
  chatbotName: string,
  companyName?: string,
  knowledgeBase?: string[],
): string => {
  let prompt = `You are an assistant for ${chatbotName}`;

  if (companyName) {
    prompt += ` at ${companyName}`;
  }

  prompt += `. Be helpful, clear, and friendly.`;

  if (knowledgeBase && knowledgeBase.length > 0) {
    prompt += `\n\nUse the following information to answer questions:\n\n${knowledgeBase.join('\n\n')}`;
  }

  return prompt;
};

/**
 * Process message content with advanced formatting
 * Sanitizes content and handles special formatting including rich media
 */
type ActionButtonTheme = {
  primarycolor?: string;
  buttontextcolor?: string;
  borderradius?: number | string;
  fontfamily?: string;
};

export const processMessageContent = (content: string, richContent?: any, buttonTheme?: ActionButtonTheme): string => {
  let processedContent = content;

  // First, replace any structured button placeholders with actual button markup
  processedContent = processActionButtons(processedContent, buttonTheme);

  // Handle different content types FIRST
  if (richContent?.content_type === 'markdown') {
    processedContent = processMarkdownContent(processedContent);
  } else if (richContent?.content_type === 'html') {
    // HTML content is processed as-is but sanitized
    processedContent = processedContent;
  } else {
    // Plain text - convert URLs to clickable links (but skip URLs that are already in HTML attributes)
    const urlRegex = /(https?:\/\/[^\s"'<>]+)/g;
    processedContent = processedContent.replace(urlRegex, (url, offset, string) => {
      // Ensure we have valid parameters
      if (typeof string !== 'string' || typeof offset !== 'number') {
        return url;
      }

      // Don't convert URLs that are already inside HTML attributes
      const beforeUrl = string.substring(0, offset);
      const afterUrl = string.substring(offset + url.length);

      // Check if URL is inside an HTML attribute (src=", href=", etc.)
      const isInAttribute = /(?:src|href|data-[^=]*)\s*=\s*["']?[^"']*$/.test(beforeUrl);
      const isFollowedByAttributeEnd = /^["'\s>]/.test(afterUrl);

      // Don't convert if it's already in an HTML attribute
      if (isInAttribute || processedContent.includes(`src="${url}`) || processedContent.includes(`href="${url}`)) {
        return url;
      }

      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="chat-link">${url}</a>`;
    });
  }

  // Process rich media elements AFTER content type processing
  if (richContent?.enabled && richContent?.media_elements) {
    processedContent = processRichMediaElements(processedContent, richContent.media_elements);
  }

  return processedContent;
};

/**
 * Process rich media elements in content
 */
export const processRichMediaElements = (content: string, mediaElements: any[]): string => {
  let processedContent = content;

  mediaElements.forEach((element, index) => {
    const placeholder = `{{media_${index}}}`;
    const placeholderRegex = new RegExp(escapeRegExp(placeholder), 'g');

    switch (element.type) {
      case 'image':
        const imageHtml = createImageElement(element);
        // Replace ALL occurrences of the placeholder
        processedContent = processedContent.replace(placeholderRegex, imageHtml);
        break;
      case 'link':
        const linkHtml = createLinkElement(element);
        // Replace ALL occurrences of the placeholder
        processedContent = processedContent.replace(placeholderRegex, linkHtml);
        break;
      case 'video':
        const videoHtml = createVideoElement(element);
        // Replace ALL occurrences of the placeholder
        processedContent = processedContent.replace(placeholderRegex, videoHtml);
        break;
    }
  });

  return processedContent;
};

const BUTTON_URL_REGEX = /\{\{\{ButtonUrl\s*->\s*buttonText\(([^)]*?)\),\s*buttonUrl\(([^)]*?)\)\s*}}}/gi;
const BUTTON_PHONE_REGEX = /\{\{\{ButtonPhone\s*->\s*phoneText\(([^)]*?)\),\s*phoneNumber\(([^)]*?)\)\s*}}}/gi;

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const toKebabCase = (value: string): string =>
  value.replace(/([a-z0-9])([A-Z])/g, '$1-$2').replace(/_/g, '-').toLowerCase();

/**
 * Remove trailing incomplete triple-brace placeholders during streaming
 * so the user doesn't see raw templates while tokens arrive.
 * Example: "text {{{ButtonUrl -> buttonText(" => will strip from last "{{{" to end.
 */
export const stripIncompletePlaceholders = (
  content: string,
  options?: { placeholderHtml?: string }
): string => {
  if (!content) return content;
  const lastOpen = content.lastIndexOf('{{{');
  const hasOpen = lastOpen !== -1;
  const hasClosingAfter = hasOpen ? content.indexOf('}}}', lastOpen) : -1;

  if (hasOpen && hasClosingAfter === -1) {
    const placeholder = options?.placeholderHtml || '';
    return content.substring(0, lastOpen) + placeholder;
  }
  return content;
};

const buildActionButtonStyle = (theme?: ActionButtonTheme): string => {
  const primary = theme?.primarycolor || '#497CFD';
  const textColor = theme?.buttontextcolor || '#FFFFFF';
  const radius =
    typeof theme?.borderradius === 'number'
      ? `${theme?.borderradius}px`
      : typeof theme?.borderradius === 'string'
        ? theme.borderradius
        : '0.5rem';
  const fontFamily = theme?.fontfamily || 'inherit';

  const styles: Record<string, string> = {
    display: 'block',
    width: 'fit-content',
    padding: '0.75rem 1.25rem',
    margin: '0.25rem 0',
    borderRadius: radius,
    backgroundColor: primary,
    color: textColor,
    border: `1px solid ${primary}`,
    cursor: 'pointer',
    opacity: '1',
    transition: 'all 0.2s ease',
    fontFamily,
    fontSize: '0.95rem',
    fontWeight: '600',
    textAlign: 'center',
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
    minWidth: '120px',
    maxWidth: '100%',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    lineHeight: '1.5',
    position: 'relative',
    textDecoration: 'none',
    userSelect: 'none',
  };

  return Object.entries(styles)
    .map(([key, value]) => `${toKebabCase(key)}: ${value}`)
    .join('; ');
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const normalizePhoneNumber = (phone: string): string => {
  const trimmed = (phone || '').trim();
  if (!trimmed) return '';

  // Keep digits and a single leading +
  const cleaned = trimmed.replace(/[^\d+]/g, '');
  const hasLeadingPlus = cleaned.startsWith('+');
  const digitsOnly = cleaned.replace(/\+/g, '');
  const normalized = `${hasLeadingPlus ? '+' : ''}${digitsOnly}`;

  // Basic length guard to avoid empty or obviously invalid values
  return normalized.length >= 3 ? normalized : '';
};

/**
 * Convert AI button placeholders into safe, stylable HTML buttons
 */
export const processActionButtons = (content: string, buttonTheme?: ActionButtonTheme): string => {
  if (!content) return content;

  let processedContent = content;
  const buttonStyle = buildActionButtonStyle(buttonTheme);

  processedContent = processedContent.replace(
    BUTTON_URL_REGEX,
    (_, buttonTextRaw: string, buttonUrlRaw: string) => {
      const buttonText = escapeHtml((buttonTextRaw || '').trim() || 'Open link');
      const buttonUrl = (buttonUrlRaw || '').trim().replace(/"/g, '%22');

      if (!buttonUrl) {
        return buttonText;
      }

      const safeDataText = buttonText;
      const safeDataValue = escapeHtml(buttonUrl);

      return `<button type="button" class="chat-action-button" style="${buttonStyle}" data-action-type="url" data-action-text="${safeDataText}" data-action-value="${safeDataValue}">${buttonText}</button>`;
    }
  );

  processedContent = processedContent.replace(
    BUTTON_PHONE_REGEX,
    (_, phoneTextRaw: string, phoneNumberRaw: string) => {
      const buttonText = escapeHtml((phoneTextRaw || '').trim() || 'Call');
      const phoneNumber = normalizePhoneNumber(phoneNumberRaw || '');

      if (!phoneNumber) {
        return buttonText;
      }

      const safeDataText = buttonText;
      const safeDataValue = escapeHtml(phoneNumber);

      return `<button type="button" class="chat-action-button chat-action-button--phone" style="${buttonStyle}" data-action-type="phone" data-action-text="${safeDataText}" data-action-value="${safeDataValue}">${buttonText}</button>`;
    }
  );

  return processedContent;
};

/**
 * Create HTML for image elements
 */
export const createImageElement = (element: any): string => {
  const { url, alt_text, link_url, width, height, position } = element;
  const style = [];

  if (width) style.push(`width: ${width}px`);
  if (height) style.push(`height: ${height}px`);

  const positionClass = position ? `chat-image-${position}` : 'chat-image-block';
  const styleAttr = style.length > 0 ? ` style="${style.join('; ')}"` : '';

  const imageTag = `<img src="${url}" alt="${alt_text || 'Image'}" class="chat-image ${positionClass}"${styleAttr} loading="lazy" />`;

  // If link_url is provided, wrap the image in an anchor tag
  if (link_url && link_url.trim()) {
    return `<a href="${link_url}" target="_blank" rel="noopener noreferrer" class="chat-image-link">${imageTag}</a>`;
  }

  return imageTag;
};

/**
 * Create HTML for link elements
 */
export const createLinkElement = (element: any): string => {
  const { url, display_text } = element;
  const linkText = display_text || url;

  return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="chat-link">${linkText}</a>`;
};

/**
 * Create HTML for video elements
 */
export const createVideoElement = (element: any): string => {
  const { url, width, height, position } = element;
  const style = [];

  if (width) style.push(`width: ${width}px`);
  if (height) style.push(`height: ${height}px`);

  const positionClass = position ? `chat-video-${position}` : 'chat-video-block';
  const styleAttr = style.length > 0 ? ` style="${style.join('; ')}"` : '';

  // Support for YouTube, Vimeo, and direct video files
  if (url.includes('youtube.com') || url.includes('youtu.be')) {
    const videoId = extractYouTubeId(url);
    return `<iframe src="https://www.youtube.com/embed/${videoId}" class="chat-video ${positionClass}"${styleAttr} frameborder="0" allowfullscreen></iframe>`;
  } else if (url.includes('vimeo.com')) {
    const videoId = extractVimeoId(url);
    return `<iframe src="https://player.vimeo.com/video/${videoId}" class="chat-video ${positionClass}"${styleAttr} frameborder="0" allowfullscreen></iframe>`;
  } else {
    return `<video controls class="chat-video ${positionClass}"${styleAttr}><source src="${url}" type="video/mp4">Your browser does not support the video tag.</video>`;
  }
};

/**
 * Process basic markdown content
 */
export const processMarkdownContent = (content: string): string => {
  let processed = content;

  // Bold text
  processed = processed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

  // Italic text
  processed = processed.replace(/\*(.*?)\*/g, '<em>$1</em>');

  // Links [text](url)
  processed = processed.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="chat-link">$1</a>');

  // Images ![alt](url)
  processed = processed.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="chat-image chat-image-block" loading="lazy" />');

  // Line breaks
  processed = processed.replace(/\n/g, '<br>');

  return processed;
};

/**
 * Extract YouTube video ID from URL
 */
export const extractYouTubeId = (url: string): string => {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
  return match ? match[1] : '';
};

/**
 * Extract Vimeo video ID from URL
 */
export const extractVimeoId = (url: string): string => {
  const match = url.match(/vimeo\.com\/(\d+)/);
  return match ? match[1] : '';
};

/**
 * Sanitize message content before rendering
 * Prevents XSS attacks while allowing safe rich media elements
 */
export const sanitizeContent = (content: string): string => {
  // Remove potential script tags
  let sanitized = content.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

  // Remove potential on* attributes (except for safe iframe sources)
  sanitized = sanitized.replace(/\s+on\w+\s*=\s*("[^"]*"|'[^']*'|[^>\s]+)/gi, '');

  // Allow specific iframe sources (YouTube, Vimeo)
  const safeIframeRegex = /<iframe\s+[^>]*src=["'](https:\/\/(?:www\.youtube\.com\/embed\/|player\.vimeo\.com\/video\/)[^"']+)["'][^>]*>.*?<\/iframe>/gi;
  const safeIframes: string[] = [];
  let iframeMatch;

  while ((iframeMatch = safeIframeRegex.exec(sanitized)) !== null) {
    safeIframes.push(iframeMatch[0]);
  }

  // Remove all iframe tags first
  sanitized = sanitized.replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '');

  // Add back safe iframes
  safeIframes.forEach((iframe, index) => {
    const placeholder = `__SAFE_IFRAME_${index}__`;
    sanitized = sanitized.replace(placeholder, iframe);
  });

  // Process links
  if (sanitized.includes('<a')) {
    const linkRegex = /<a\s+(?:[^>]*?\s+)?href=["']([^"']*)["'][^>]*>(.*?)<\/a>/gi;
    let match;
    let processedContent = sanitized;

    while ((match = linkRegex.exec(sanitized)) !== null) {
      const fullTag = match[0];
      const url = match[1];
      const text = match[2];
      const classMatch = fullTag.match(/class=["']([^"']*)["']/i);
      const dataActionTypeMatch = fullTag.match(/data-action-type=["']([^"']*)["']/i);
      const dataActionTextMatch = fullTag.match(/data-action-text=["']([^"']*)["']/i);
      const dataActionValueMatch = fullTag.match(/data-action-value=["']([^"']*)["']/i);

      // Validate URL
      if (isValidUrl(url)) {
        const classAttr = classMatch ? classMatch[1] : 'chat-link';
        const dataAttrs = [
          dataActionTypeMatch ? ` data-action-type="${dataActionTypeMatch[1]}"` : '',
          dataActionTextMatch ? ` data-action-text="${dataActionTextMatch[1]}"` : '',
          dataActionValueMatch ? ` data-action-value="${dataActionValueMatch[1]}"` : '',
        ].join('');

        const safeLink = `<a href="${url}" target="_blank" rel="noopener noreferrer" class="${classAttr}"${dataAttrs}>${text}</a>`;
        processedContent = processedContent.replace(fullTag, safeLink);
      } else {
        // Remove invalid links
        processedContent = processedContent.replace(fullTag, text);
      }
    }

    sanitized = processedContent;
  }

  // Process images
  if (sanitized.includes('<img')) {
    const imgRegex = /<img\s+[^>]*src=["']([^"']*)["'][^>]*>/gi;
    let match;
    let processedContent = sanitized;

    while ((match = imgRegex.exec(sanitized)) !== null) {
      const fullTag = match[0];
      const url = match[1];

      // Validate image URL
      if (isValidUrl(url)) {
        // Extract alt text and other safe attributes
        const altMatch = fullTag.match(/alt=["']([^"']*)["']/i);
        const altText = altMatch ? altMatch[1] : 'Image';

        const classMatch = fullTag.match(/class=["']([^"']*)["']/i);
        const className = classMatch ? classMatch[1] : 'chat-image chat-image-block';

        const styleMatch = fullTag.match(/style=["']([^"']*)["']/i);
        const style = styleMatch ? ` style="${styleMatch[1]}"` : '';

        const safeImg = `<img src="${url}" alt="${altText}" class="${className}" loading="lazy"${style} />`;
        processedContent = processedContent.replace(fullTag, safeImg);
      } else {
        // Remove invalid images
        processedContent = processedContent.replace(fullTag, '');
      }
    }

    sanitized = processedContent;
  }

  // Process videos
  if (sanitized.includes('<video')) {
    const videoRegex = /<video\s+[^>]*>(.*?)<\/video>/gi;
    let match;
    let processedContent = sanitized;

    while ((match = videoRegex.exec(sanitized)) !== null) {
      const fullTag = match[0];
      const sourceMatch = fullTag.match(/<source\s+[^>]*src=["']([^"']*)["'][^>]*>/i);

      if (sourceMatch && isValidUrl(sourceMatch[1])) {
        const url = sourceMatch[1];
        const classMatch = fullTag.match(/class=["']([^"']*)["']/i);
        const className = classMatch ? classMatch[1] : 'chat-video chat-video-block';

        const styleMatch = fullTag.match(/style=["']([^"']*)["']/i);
        const style = styleMatch ? ` style="${styleMatch[1]}"` : '';

        const safeVideo = `<video controls class="${className}"${style}><source src="${url}" type="video/mp4">Your browser does not support the video tag.</video>`;
        processedContent = processedContent.replace(fullTag, safeVideo);
      } else {
        // Remove invalid videos
        processedContent = processedContent.replace(fullTag, '');
      }
    }

    sanitized = processedContent;
  }

  // Allow safe HTML tags for formatting and rich media
  const allowedTags = ['strong', 'em', 'br', 'p', 'div', 'span', 'img', 'a', 'video', 'source', 'iframe', 'button'];
  const tagRegex = /<\/?(\w+)[^>]*>/g;

  sanitized = sanitized.replace(tagRegex, (match, tagName) => {
    if (allowedTags.includes(tagName.toLowerCase())) {
      return match;
    }
    return '';
  });

  return sanitized;
};

/**
 * Validate if a URL is safe to use
 */
export const isValidUrl = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url);

    // Only allow http and https protocols
    const allowedProtocols = ['http:', 'https:', 'tel:', 'mailto:'];
    if (!allowedProtocols.includes(parsedUrl.protocol)) {
      return false;
    }

    if (parsedUrl.protocol === 'tel:') {
      const digitsOnly = parsedUrl.pathname.replace(/[^\d]/g, '');
      return digitsOnly.length >= 3;
    }

    // Block localhost and internal IPs in production
    if (
      ['http:', 'https:'].includes(parsedUrl.protocol) &&
      typeof window !== 'undefined' &&
      window.location.hostname !== 'localhost'
    ) {
      const hostname = parsedUrl.hostname.toLowerCase();

      if (
        hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        /^192\.168\./.test(hostname) ||
        /^10\./.test(hostname) ||
        /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(hostname)
      ) {
        return false;
      }
    }

    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Validate webhook URLs
 * Prevents malicious URLs
 */
export const isValidWebhookUrl = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url);

    // Block localhost and internal IPs in production
    if (process.env.NODE_ENV === 'production') {
      const hostname = parsedUrl.hostname.toLowerCase();

      if (
        hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        /^192\.168\./.test(hostname) ||
        /^10\./.test(hostname) ||
        /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(hostname)
      ) {
        return false;
      }
    }

    return true;
  } catch (e) {
    return false;
  }
};
